import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, FlaskConical, Info, X } from 'lucide-react';
import { reportService } from '../../services/reportService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import ComparePicker from './ComparePicker';

// =============================================================================
// The report page every report gets for free.
//
// A dataset that returns { kpis, columns, rows, caveats } is rendered here —
// KPI strip, grid, footnotes — without a line of page code per report. That is
// what makes a catalogue of forty reports a realistic thing to have: the
// argument about a report becomes an argument about its dataset, and the
// layout is settled once.
//
// A report that genuinely needs a bespoke layout still gets one; SKU
// Profitability has its own page and keeps it. This is the default, not a
// ceiling.
//
// MOCK DATA IS ANNOUNCED, LOUDLY. Most of these reports have a layout before
// they have a query, and a plausible number nobody flagged is exactly how a
// mock ends up in a board pack. The banner is not dismissible.
// =============================================================================

const fmtMoney = (v) =>
  v == null ? '—' : new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 }).format(v);
const fmtQty = (v) =>
  v == null ? '—' : new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 }).format(v);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtInt = (v) => (v == null ? '—' : new Intl.NumberFormat('en-ZA').format(Math.round(v)));

const fmtByKind = (kind, v) => {
  if (v == null || v === '') return '—';
  if (kind === 'money') return fmtMoney(v);
  if (kind === 'qty') return fmtQty(v);
  if (kind === 'pct') return fmtPct(v);
  if (kind === 'int') return fmtInt(v);
  return String(v);
};

// Negative money reads as a bracket, the way every finance report does it.
const fmtSignedMoney = (v) => (v == null ? '—' : v < 0 ? `(${fmtMoney(Math.abs(v))})` : fmtMoney(v));

export default function GenericReport() {
  const navigate = useNavigate();
  const { sectionCode, reportCode } = useParams();
  const { selectedVersion, selectedSite } = useAppContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [compare, setCompare] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportService.getReportData(reportCode, compare ? { compare } : {});
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not run the report.');
      // A refused comparison must not leave its columns on screen mislabelled.
      if (compare) setCompare('');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [reportCode, compare]);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion, selectedSite]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const baseColumns = useMemo(() => data?.columns || [], [data]);
  const comparing = !!data?.compare && rows.some((r) => r.variance);

  // When comparing, each numeric column becomes three: this version, the other
  // one, and the movement. Built here rather than in the dataset so a report
  // does not have to know whether it is being compared.
  const columns = useMemo(() => {
    if (!comparing) return baseColumns.map((c) => ({ ...c, width: c.kind === 'text' ? 150 : 118 }));
    const out = [];
    baseColumns.forEach((c) => {
      if (c.kind === 'text' || c.kind === 'flag') { out.push({ ...c, width: 150 }); return; }
      if (!(data.variance_keys || []).includes(c.key)) { out.push({ ...c, width: 118 }); return; }
      out.push({ ...c, name: c.name, width: 118, slot: 'base' });
      out.push({ ...c, name: `${c.name} (cmp)`, width: 118, slot: 'compare' });
      out.push({ ...c, name: `${c.name} Δ`, width: 118, slot: 'variance' });
    });
    return out;
  }, [baseColumns, comparing, data]);

  const gridColumns = useMemo(
    () => columns.map((c) => ({ id: c.key + (c.slot || ''), title: c.name, width: c.width })),
    [columns]
  );

  const getCellContent = useCallback(([colIdx, rowIdx]) => {
    const c = columns[colIdx];
    const r = rows[rowIdx];
    if (!c || !r) return gcell.text('');

    const value =
      c.slot === 'compare' ? r.compare?.[c.key]
        : c.slot === 'variance' ? r.variance?.[c.key]
          : r[c.key];

    if (c.kind === 'flag') return gcell.boolean(!!value, { readonly: true });
    if (c.kind === 'text') return gcell.text(value == null ? '' : String(value), { readonly: true });

    const negative = typeof value === 'number' && value < 0;
    const display =
      c.kind === 'money' ? fmtSignedMoney(value)
        : c.kind === 'pct' ? fmtPct(value)
          : fmtQty(value);

    return gcell.number(value ?? 0, {
      readonly: true,
      display,
      theme: negative ? { textDark: '#dc2626' } : undefined,
    });
  }, [columns, rows]);

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to run this report.</p>
      </div>
    );
  }

  const kpis = data?.kpis || [];
  const caveats = data?.caveats || {};

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate(`/reports/${sectionCode}`)}>
              <ArrowLeft className="w-3.5 h-3.5" /> {data?.report?.section_name || 'Back'}
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              {data?.report?.report_name || reportCode}
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
            <span className="text-xs text-slate-400">{selectedSite || 'All sites'}</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <ComparePicker reportCode={reportCode} value={compare} onChange={setCompare} />
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400 disabled:opacity-50"
              onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Not dismissible, and above the numbers rather than below them. */}
        {data?.mock && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <FlaskConical className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
              <p className="m-0 text-xs font-bold text-amber-800 dark:text-amber-300">
                Illustrative layout — these numbers are generated, not measured.
              </p>
              <p className="m-0 mt-0.5 text-[11px] text-amber-700 dark:text-amber-400/90">
                {data.mock_note} The column set, units and totals are the thing to review; the figures are not real
                and must not be quoted.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {kpis.length > 0 && !loading && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {kpis.map((t) => <Figure key={t.label} {...t} />)}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 max-h-full flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : error ? (
            <div className="py-10 px-6 text-center">
              <p className="m-0 text-sm font-semibold text-red-700 dark:text-red-300">The report could not be run.</p>
              <p className="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              Nothing to report for {selectedVersion}{selectedSite ? ` at ${selectedSite}` : ''}.
            </p>
          ) : (
            <MantleDataGrid
              gridId={`report-${reportCode}`}
              fitHeight
              fitHeightPadding={6}
              columns={gridColumns}
              rowCount={rows.length}
              getCellContent={getCellContent}
              freezeColumns={1}
              hint="Read-only. Brackets are deductions. Ctrl+F searches, and columns drag to resize."
              toolbar={
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {rows.length} row{rows.length === 1 ? '' : 's'}
                  {comparing && ` · comparing against ${data.compare_label || data.compare}`}
                </span>
              }
            />
          )}
        </div>

        {/* The footnotes are the report. Half of these say which costs are
            deliberately excluded, and a reader who does not see that will
            reconcile to a different number and trust neither. */}
        {!loading && !error && Object.keys(caveats).length > 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Info className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">How to read this</span>
            </div>
            <ul className="m-0 pl-4 space-y-1.5">
              {Object.entries(caveats).map(([k, v]) => (
                <li key={k} className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">
                  {typeof v === 'string' ? v : `${k}: ${JSON.stringify(v)}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

const TONES = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  bad: 'border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
  warn: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
};

const Figure = ({ label, value, sub, tone, kind }) => (
  <div className={`inline-flex flex-col px-3 py-1.5 rounded-md border ${TONES[tone] || 'border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100'}`}>
    <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</span>
    <span className="text-sm font-bold tabular-nums">
      {kind === 'money' || !kind ? fmtSignedMoney(value) : fmtByKind(kind, value)}
    </span>
    {sub && <span className="text-[10px] opacity-70">{sub}</span>}
  </div>
);
